import Konva from "konva";
import useImage from "use-image";
import { KonvaEventObject } from "konva/lib/Node";
import { useRef, useState, useEffect } from "react";
import { Stage, Layer, Image } from "react-konva";
import { Rectangle } from "@/components/ui/canvas";
import { Button } from "@/components/ui/button";
import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { useCanvasContain } from "./use-canvas-contain";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { cn } from "@/utils/classes";
import { Dropzone } from "@/api/studio/contexts/types/context.type";
import { Toggle } from "@/components/ui/form";
import { useSegment } from "@/hooks/use-segment";
import { Point } from "@/api/studio/data/types/point.type";

/* Constants */
const SCALE_BY = 1.1;

/* Prop Types */
interface EditorProps {
  imageUrl: string;
  contextId: string;
  assetSize: { width: number; height: number };
  dropzone: Dropzone;
  className?: string;
  dropzoneBackground: string;
  setDropzoneBackground: (color: string) => void;
  showDropzoneBackground: boolean;
  setShowDropzoneBackground: (show: boolean) => void;
  setPoints: (points: Point[]) => void;
  setDropzoneId: (id: string) => void;
}

/*
 * ContextCanvas component
 * Uses Konva to render the asset and allow the user to add a dropzone
 */
export const ContextCanvas = ({
  imageUrl,
  assetSize,
  dropzone,
  className,
  contextId,
  dropzoneBackground,
  setDropzoneBackground,
  showDropzoneBackground,
  setShowDropzoneBackground,
  setPoints,
  setDropzoneId,
}: EditorProps) => {
  /* Refs */
  const stageRef = useRef<Konva.Stage>(null);

  /* State */
  const [scale, setScale] = useState(1);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);

  /* Custom hooks */
  const { canvasSize, ratio, position, editorRef, setPosition, posRef } = useCanvasContain(assetSize);
  const { segmentTrackEvent } = useSegment();

  /* Effects */
  useEffect(() => {
    /* Reset zoom and position when image URL changes */
    handleZoomReset();
  }, [imageUrl]);

  /* Handlers */
  const handleZoomIn = () => {
    setScale(scale * SCALE_BY);
  };

  const handleZoomOut = () => {
    setScale(scale / SCALE_BY);
  };

  const handleZoomReset = () => {
    setScale(1);
    setPosition(posRef.current);
    stageRef.current?.position(posRef.current);
  };

  const handleZoomToCursor = (e: KonvaEventObject<WheelEvent>) => {
    const pointer = stageRef?.current?.getPointerPosition();

    const mousePoint = {
      x: (pointer!.x - stageRef.current!.x()) / scale,
      y: (pointer!.y - stageRef.current!.y()) / scale,
    };

    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = direction > 0 ? scale * SCALE_BY : scale / SCALE_BY;

    setScale(newScale);

    setPosition({
      x: pointer!.x - mousePoint.x * newScale,
      y: pointer!.y - mousePoint.y * newScale,
    });
  };

  const handleContextMenu = (e: KonvaEventObject<PointerEvent>) => {
    e.evt.preventDefault();
  };

  const handleDropzoneBackground = (e: React.ChangeEvent<HTMLInputElement>) => {
    const color = e.target.value;
    setDropzoneBackground(color);
  };

  const handleShowBackgroundToggle = () => {
    setShowDropzoneBackground(!showDropzoneBackground);
    setDropzoneBackground(!showDropzoneBackground ? dropzoneBackground : "#FFFFFF00");

    // Segment tracking
    segmentTrackEvent("Context Dropzone Background Toggled", {
      contextId: contextId,
      value: !showDropzoneBackground,
    });
  };

  const handleRegionSave = async (id: string, x: number, y: number, width: number, height: number) => {
    // Round to integers
    x = Math.round(x);
    y = Math.round(y);
    width = Math.round(width);
    height = Math.round(height);

    // Create the region
    const points = [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height },
    ];

    // save the dropzone against the context
    setPoints(points);
    setDropzoneId(id);
  };

  /* Helpers */
  const [image] = useImage(imageUrl);

  /* Render */
  return (
    <div className={cn("grow flex flex-row relative overflow-hidden h-full", className)} ref={editorRef}>
      {/* Controls */}
      <div className="flex flex-row gap-2 absolute right-4 top-4 z-10">
        <div className="flex flex-row flex-wrap gap-2 items-center bg-base-200 p-3 rounded-lg shadow-lg max-w-[168px]">
          <p className="text-sm">Fill background</p>
          <Toggle checked={showDropzoneBackground} variant="primary" size="small" onChange={handleShowBackgroundToggle} />
          {showDropzoneBackground && (
            <div className="flex flex-row gap-2 items-center">
              <input type="color" defaultValue={dropzoneBackground} name="" id="" className="size-5 rounded-md cursor-pointer" onChange={handleDropzoneBackground} />
              <p className="text-sm">{dropzoneBackground.toUpperCase()}</p>
            </div>
          )}
        </div>
        <CustomDropdown
          className="p-0 shadow-lg"
          triggerBtnClassname="p-0"
          align="end"
          triggerBtn={
            <Button>
              {Math.round(scale * 100)}% <ChevronDownIcon className="size-3.5 inline-block" />
            </Button>
          }
        >
          <CustomDropdownItem onClick={handleZoomIn}>Zoom in</CustomDropdownItem>
          <CustomDropdownItem onClick={handleZoomOut}>Zoom out</CustomDropdownItem>
          <CustomDropdownItem onClick={handleZoomReset}>Fit to canvas</CustomDropdownItem>
        </CustomDropdown>
      </div>

      {/* Stage */}
      <Stage
        ref={stageRef}
        tabIndex={0}
        width={editorRef.current?.clientWidth}
        height={editorRef.current?.clientHeight}
        scale={{ x: scale, y: scale }}
        position={position}
        onWheel={handleZoomToCursor}
        draggable
        onContextMenu={handleContextMenu}
      >
        <Layer>
          <Image image={image} width={canvasSize.width} height={canvasSize.height} />
        </Layer>
        <Layer>
          <Rectangle
            id={contextId}
            points={dropzone.points}
            label="Dropzone"
            labelColor="#F2318A"
            strokeColor="#F2318A"
            backgroundColor={showDropzoneBackground ? dropzoneBackground : dropzone.backgroundColor}
            isSelected={contextId === selectedRegion}
            ratio={ratio}
            canvasSize={canvasSize}
            handleRegionSave={handleRegionSave}
            setSelectedRegion={setSelectedRegion}
            scale={scale}
          />
        </Layer>
      </Stage>
    </div>
  );
};
